const BAR_WIDTH = 3;
const BAR_GAP = 2;
/** The graph advances one bar per interval, so it scrolls at the same speed on every display. */
const SAMPLE_MS = 60;
/** Speech usually sits between these loudness levels; anything quieter rests on the baseline. */
const FLOOR_DB = -55;
const CEILING_DB = -12;

/**
 * Draws a scrolling loudness graph of `stream` on `canvas`, in the canvas's text colour, read from
 * the existing microphone stream without owning its tracks. Each bar's level is also added to
 * `history`. Returns the function that stops it.
 */
export function drawSpeechGraph(
  canvas: HTMLCanvasElement,
  stream: MediaStream,
  history: number[] = [],
): () => void {
  if (typeof AudioContext === 'undefined') return () => undefined;
  const paint = canvas.getContext('2d');
  if (!paint) return () => undefined;
  let context: AudioContext | undefined;
  let source: MediaStreamAudioSourceNode | undefined;
  let analyser: AnalyserNode | undefined;
  let frame = 0;
  function release() {
    cancelAnimationFrame(frame);
    source?.disconnect();
    analyser?.disconnect();
    if (context && context.state !== 'closed')
      void context.close().catch(() => {
        // The browser may already have released the audio context.
      });
  }
  try {
    context = new AudioContext();
    source = context.createMediaStreamSource(stream);
    analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    const samples = new Float32Array(analyser.fftSize);
    const levels: number[] = [];
    const color = getComputedStyle(canvas).color;
    // Browsers without rounded rectangles (Safari before 16) draw square bars instead.
    const rounded = typeof (paint as Partial<typeof paint>).roundRect === 'function';
    const meter = analyser;
    let peak = 0;
    let sampledAt: number | undefined;
    const draw = (time: number) => {
      meter.getFloatTimeDomainData(samples);
      let power = 0;
      for (const sample of samples) power += sample * sample;
      const decibels = 10 * Math.log10(power / samples.length);
      const level = (decibels - FLOOR_DB) / (CEILING_DB - FLOOR_DB);
      // Keep the loudest moment between bars so short syllables still register.
      peak = Math.max(peak, Math.min(1, Math.max(0, level)));
      sampledAt ??= time;
      if (time - sampledAt >= SAMPLE_MS) {
        levels.push(peak);
        history.push(peak);
        peak = 0;
        sampledAt = time;
      }

      const ratio = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (canvas.width !== Math.round(width * ratio)) canvas.width = Math.round(width * ratio);
      if (canvas.height !== Math.round(height * ratio)) canvas.height = Math.round(height * ratio);
      paint.setTransform(ratio, 0, 0, ratio, 0, 0);
      paint.clearRect(0, 0, width, height);
      paint.fillStyle = color;
      paint.beginPath();
      const step = BAR_WIDTH + BAR_GAP;
      const count = Math.floor((width + BAR_GAP) / step);
      if (levels.length > count) levels.splice(0, levels.length - count);
      // The newest bar sits at the right edge; silence draws as a row of dots.
      for (let bar = 0; bar < count; bar++) {
        const barHeight = Math.max(BAR_WIDTH, (levels[levels.length - count + bar] ?? 0) * height);
        const x = width - (count - bar) * step + BAR_GAP;
        const y = (height - barHeight) / 2;
        if (rounded) paint.roundRect(x, y, BAR_WIDTH, barHeight, BAR_WIDTH / 2);
        else paint.rect(x, y, BAR_WIDTH, barHeight);
      }
      paint.fill();
      frame = requestAnimationFrame(draw);
    };
    void context.resume().catch(() => {
      // A blocked visualizer must not interrupt the recording.
    });
    frame = requestAnimationFrame(draw);
  } catch {
    // Visualization support must never prevent dictation.
    release();
  }
  return release;
}
